import { FC, useEffect } from "react";
import { DbotEvent, DbotEventDispatcher, ProjectContext } from "../../dbot/common/commonDomain";
import {
  AdminElectronicGeneralMeetingController,
  AttendanceData,
  ElectronicGeneralMeeting,
  GeneralMeetingStatus,
  isAttending,
  ShareholderAttendanceRow,
  StatusName,
  totalNumberOfVotes,
} from "./electronicGeneralMeetingDomain";
import { observer } from "mobx-react";
import { Dialog } from "../../components-v2/Dialog";
import { Box, VBox } from "../../components-v2/Box";
import { MTextInput } from "../../components-v2/TextInput";
import { MSelect } from "../../components-v2/Select";
import { Button, IconButton, LinkButton, ToggleButton } from "../../components-v2/Button";
import { User } from "../../types/UserInfo";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { ExtendedShareholderDTO, totalNumberOfSharesOfShareholders } from "../../types/Shareholder";
import { Icon } from "../../components-v2/Icon";
import { IconName } from "../../components-v2/iconMapping";
import { percentOf } from "../../utils/numbers";
import { HoverTooltip } from "../../components-v2/Tooltip";
import {
  GeneralMeetingAttendanceDialog,
  GeneralMeetingStatusTag,
} from "./AdminElectronicGeneralMeetingPage";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { adminShareholderDetails, openLinkInNewWindow } from "../../utils/navigation";
import { TMessageKey } from "../../i18n/types.gen";
import { useLocaleFormat, useTranslation } from "../../stores/RootStoreContext";
import { ShareType } from "../../common/share";
import { registrationLabel } from "./generalMeetingDomain";

export const AttendanceDialog: FC<{
  projectId: string;
  context: ProjectContext;
  controller: AdminElectronicGeneralMeetingController;
  registrationStatus?: GeneralMeetingStatus;
  data: AttendanceData;
  meeting: ElectronicGeneralMeeting;
  dispatch: DbotEventDispatcher;
  next?: DbotEvent[];
  prev?: DbotEvent[];
}> = observer(
  ({ projectId, context, controller, registrationStatus, data, meeting, dispatch, next, prev }) => {
    const { t } = useTranslation();

    useEffect(() => {
      controller.loadPowerOfAttorneyData(projectId);
      controller.loadNoticeForShareholdersUrl(projectId);
    }, [controller, projectId]);

    return (
      <Dialog
        title={t("general-meeting.attendance.attendance")}
        fullscreen
        onCancel={() => controller.hideGeneralMeetingDialogs(meeting.participationList)}
        actions={<div />}
        headerElement={
          <Box alignItems={"center"} justifyContent={"space-between"} grow>
            <Box gap={"md"}>
              <GeneralMeetingStatusTag status={registrationStatus} />
              <LinkButton
                label={t(registrationLabel(registrationStatus))}
                onClick={() =>
                  controller.updateMeetingStatus({
                    statusName: StatusName.REGISTRATION,
                    meetingStatus: meeting.registrationStatus,
                    participationList: meeting.participationList,
                  })
                }
              />
            </Box>
            <Box alignItems={"center"}>
              <IconButton
                icon={"caret-down"}
                color={"inherit"}
                tooltip={t("next")}
                disabled={!next}
                onClick={() => {
                  if (next !== undefined) {
                    controller.sendUpdateMinutesLiveParticipationEvent(meeting.participationList);
                    dispatch(next);
                  }
                }}
              />

              <IconButton
                icon={"caret-up"}
                color={"inherit"}
                tooltip={t("previous")}
                disabled={!prev}
                onClick={() => {
                  if (prev !== undefined) {
                    controller.sendUpdateMinutesLiveParticipationEvent(meeting.participationList);
                    dispatch(prev);
                  }
                }}
              />
            </Box>
          </Box>
        }
      >
        <VBox>
          <SearchFilterInputBar controller={controller} data={data} />
          <ShareholderAttendanceTable
            controller={controller}
            user={context.user}
            data={data}
            meeting={meeting}
            context={context}
          />
        </VBox>
        <GeneralMeetingAttendanceDialog dialogState={controller.attendanceDialogState} />
      </Dialog>
    );
  },
);

const SearchFilterInputBar: FC<{
  controller: AdminElectronicGeneralMeetingController;
  data: AttendanceData;
}> = observer(({ controller, data }) => {
  const { t } = useTranslation();

  return (
    <Box justifyContent={"space-between"}>
      <Box gap={"xl"}>
        <Box>
          <MTextInput
            model={controller.shareholderNameFilter}
            placeholder={t("general-meeting.attendance.shareholderNameFilter")}
            autofocus
            inline
            type={"search"}
          />
          <Box>
            <MSelect model={controller.shareTypeFilter} fullWidth={false} />
          </Box>
        </Box>
        <Box alignItems={"center"}>
          <FilterToggleButton
            icon={"calendar-check"}
            shareholders={controller.allShareholders}
            shareTypes={controller.shareholderData.shareTypes}
            nominalValue={controller.shareNominalValue}
            headingLabel={"general-meeting.attendance.recipients"}
            selected={controller.shareholderAttendanceFilter === "all"}
            onClick={() => controller.setSelectedAttendanceFilter("all")}
          />
          <FilterToggleButton
            icon={"question"}
            shareholders={data.shareholdersNotAttending}
            shareTypes={controller.shareholderData.shareTypes}
            nominalValue={controller.shareNominalValue}
            headingLabel={"general-meeting.attendance.recipientsNotAttending"}
            totalShareCapital={controller.totalShareCapital}
            selected={controller.shareholderAttendanceFilter === "notAttending"}
            onClick={() => controller.setSelectedAttendanceFilter("notAttending")}
          />
          <FilterToggleButton
            icon={"check-circle"}
            shareholders={data.attendingShareholders}
            shareTypes={controller.shareholderData.shareTypes}
            nominalValue={controller.shareNominalValue}
            headingLabel={"general-meeting.attendance.recipientsAttending"}
            totalShareCapital={controller.totalShareCapital}
            selected={controller.shareholderAttendanceFilter === "attending"}
            onClick={() => controller.setSelectedAttendanceFilter("attending")}
          />
          <FilterToggleButton
            icon={"file-text"}
            shareholders={controller.shareholderData.shareholders.filter((s) =>
              controller.hasPoa(s.id),
            )}
            shareTypes={controller.shareholderData.shareTypes}
            nominalValue={controller.shareNominalValue}
            headingLabel={"general-meeting.attendance.recipientsWithPoa"}
            totalShareCapital={controller.totalShareCapital}
            selected={controller.shareholderAttendanceFilter === "poa"}
            onClick={() => controller.setSelectedAttendanceFilter("poa")}
          />
        </Box>
      </Box>
      <Box>
        <Button
          icon={"file-plus"}
          label={t("general-meeting.attendance.powerOfAttorney.register")}
          variant={"secondary"}
          size={"xs"}
          onClick={() => controller.openRegisterPowerOfAttorneyDialog()}
        />
      </Box>
    </Box>
  );
});

const ShareholderAttendanceTable: FC<{
  user: User;
  controller: AdminElectronicGeneralMeetingController;
  data: AttendanceData;
  meeting: ElectronicGeneralMeeting;
  context: ProjectContext;
}> = observer(({ user, controller, data, meeting, context }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();
  const tableColumns: TableColumn<ShareholderAttendanceRow>[] = [
    {
      name: "name",
      label: t(controller.shareholderAttendanceColumnLabel),
      align: "left",
      getValue: (s) => (
        <ShareholderNameDetails name={s.name} isAttending={isAttending(data, s.shareholderId)} />
      ),
    },
    {
      name: "representative",
      label: t("general-meeting.attendance.representative"),
      align: "left",
      getValue: (s) =>
        controller.hasPoa(s.shareholderId) ? (
          <VBox gap={0}>
            <div>{s.representative}</div>
            <small className="subtle">{t("general-meeting.attendance.powerOfAttorney")}</small>
          </VBox>
        ) : (
          s.representative
        ),
    },
    {
      name: "attendance",
      label: t("general-meeting.attendance.attendance"),
      align: "left",
      getValue: (s) => (
        <ToggleAttendanceButton
          meeting={meeting}
          shareholderId={s.shareholderId}
          controller={controller}
          user={user}
        />
      ),
    },
    {
      name: "birthDateOrOrganisationNumber",
      label: t("general-meeting.birthDateOrOrganisationNumber"),
      align: "left",
    },
    {
      name: "numberOfShares",
      label: t("general-meeting.attendance.numberOfShares"),
      getValue: (s) => formatNumber(s.numberOfShares),
    },
    {
      name: "percentageOfShares",
      label: t("general-meeting.attendance.percentageOfShares"),
      getValue: (s) => formatPercent(s.percentageOfShares),
    },
    {
      name: "shareCapital",
      label: t("general-meeting.attendance.shareCapital"),
      getValue: (s) => formatNumber(s.shareCapital, 2),
    },
    {
      name: "email",
      label: t("general-meeting.attendance.email"),
      align: "left",
    },
    {
      name: "actions",
      label: "",
      getValue: (s) => (
        <DropdownMenu
          button={{ type: "dotdotdot" }}
          items={[
            {
              type: "label",
              label: t(
                s.attendance
                  ? "general-meeting.attendance.recipientsAttending.label"
                  : "general-meeting.attendance.recipientsNotAttending.label",
                {
                  name: s.name,
                },
              ),
            },
            {
              type: "item",
              label: t(
                s.attendance
                  ? "general-meeting.attendance.reset-attendance"
                  : "general-meeting.attendance.register-button",
              ),
              icon: s.attendance ? "arrow-counter-clockwise" : "check",
              onSelect: () =>
                s.attendance
                  ? controller.registerNotAttending(s.shareholderId, meeting)
                  : controller.registerAttending(s.shareholderId, meeting),
              isDisabled:
                !s.attendance && controller.isRegisteringAttendanceForShareholder(s.shareholderId),
            },
            {
              type: "item",
              label: controller.hasPoa(s.shareholderId)
                ? t("general-meeting.attendance.powerOfAttorney.edit", {
                    name: s.representative,
                  })
                : t("general-meeting.attendance.powerOfAttorney.register"),
              icon: controller.hasPoa(s.shareholderId) ? "note-pencil" : "file-plus",
              onSelect: () => controller.openRegisterPowerOfAttorneyDialog(s.shareholderId),
            },
            {
              type: "item",
              label: t("general-meeting.resendNotice.title", { name: s.name }),
              icon: "paper-plane",
              isDisabled: !meeting.noticeId,
              onSelect: () => controller.openResendNoticeDialog(s),
            },
            {
              type: "item",
              label: t("general-meeting.copy-participation-link.title"),
              icon: "link",
              isDisabled: !controller.noticeForShareholdersUrl.has(s.shareholderId),
              onSelect: () => controller.openCopyNoticeUrlDialog(s),
            },
            {
              type: "item",
              label: t("general-meeting.attendance.view-shareholder-details"),
              icon: "arrow-square-out",
              onSelect: () =>
                openLinkInNewWindow(
                  adminShareholderDetails(
                    context.companyInfo.company.organizationnumber,
                    s.shareholderId,
                  ),
                ),
            },
          ]}
          alignment={"end"}
        />
      ),
    },
  ];

  return (
    <DataTable
      variant={"COMPACT"}
      tableColumns={tableColumns}
      tableData={controller.createAttendanceTableData(
        controller.filterAttendanceShareholders(data),
        data,
      )}
      getId={(row) => row.id}
    />
  );
});

function ShareholderNameDetails({ name, isAttending }: { name: string; isAttending: boolean }) {
  const { t } = useTranslation();
  return (
    <Box alignItems={"center"} gap={"sm"}>
      {isAttending ? (
        <Icon name={"check-circle"} weight={"fill"} color={"success"} />
      ) : (
        <Icon name={"question"} color={"subtle"} />
      )}
      <VBox gap={0}>
        <div>{name}</div>
        <small className="subtle">
          {isAttending
            ? t("general-meeting.attendance.isAttending")
            : t("general-meeting.attendance.isNotAttending")}
        </small>
      </VBox>
    </Box>
  );
}

const ToggleAttendanceButton: FC<{
  controller: AdminElectronicGeneralMeetingController;
  shareholderId: number;
  user: User;
  meeting: ElectronicGeneralMeeting;
}> = observer(({ shareholderId, controller, user, meeting }) => {
  const { t } = useTranslation();
  const participation = meeting?.participationList.find((p) => p.shareholderId === shareholderId);
  return participation?.isPresent ? (
    <Box alignItems={"center"}>
      {participation?.registeredBy === user.userId
        ? t("general-meeting.attendance.registeredByYou", {
            time: { type: "time", value: participation.registeredAt },
          })
        : t("general-meeting.attendance.registeredBySelf", {
            time: { type: "time", value: participation.registeredAt },
          })}
      <IconButton
        icon={"arrow-counter-clockwise"}
        tooltip={t("reset")}
        onClick={() => controller.registerNotAttending(shareholderId, meeting)}
      />
    </Box>
  ) : (
    <Button
      label={t("general-meeting.attendance.register-button")}
      variant={"secondary"}
      size={"sm"}
      icon={"check"}
      onClick={() => controller.registerAttending(shareholderId, meeting)}
      loading={controller.isRegisteringAttendanceForShareholder(shareholderId)}
    />
  );
});

export function FilterToggleButton({
  icon,
  headingLabel,
  shareholders,
  shareTypes,
  nominalValue,
  totalShareCapital,
  selected,
  onClick,
}: {
  selected?: boolean;
  icon: IconName;
  headingLabel: TMessageKey;
  shareholders: ExtendedShareholderDTO[];
  shareTypes: ShareType[];
  nominalValue: number;
  onClick: () => void;
  totalShareCapital?: number;
}) {
  const { t } = useTranslation();
  const { formatPercent, formatNumber } = useLocaleFormat();

  const numberOfShares = totalNumberOfSharesOfShareholders(shareholders);
  const shareCapital = numberOfShares * nominalValue;
  const percentageShareCapital =
    totalShareCapital !== undefined && formatPercent(percentOf(shareCapital, totalShareCapital));

  const numberOfVotes = totalNumberOfVotes(shareholders, shareTypes);

  const buttonText =
    totalShareCapital === undefined
      ? t("showAll")
      : `${shareholders.length} / ${percentageShareCapital}`;

  return (
    <HoverTooltip
      content={
        <VBox textSize={"sm"}>
          <b>{t(headingLabel)}</b>
          <table>
            <tbody>
              <tr>
                <td width={150}>{t("general-meeting.attendance.shareholders")}</td>
                <td align={"right"}>{formatNumber(shareholders.length)}</td>
              </tr>
              <tr>
                <td width={150}>{t("general-meeting.attendance.votes")}</td>
                <td align={"right"}>{formatNumber(numberOfVotes)}</td>
              </tr>
              <tr>
                <td width={150}>{t("general-meeting.attendance.shareCapital")}</td>
                <td align={"right"}>{formatNumber(shareCapital, 2)}</td>
              </tr>
              {percentageShareCapital && (
                <tr>
                  <td width={150}>
                    {t("general-meeting.attendance.percentageOfTotalshareCapital")}
                  </td>
                  <td align={"right"}>{percentageShareCapital}</td>
                </tr>
              )}
            </tbody>
          </table>
        </VBox>
      }
    >
      {({ targetRef }) => (
        <ToggleButton
          ref={targetRef}
          variant={"small"}
          icon={icon}
          label={buttonText}
          selected={selected}
          onClick={onClick}
        />
      )}
    </HoverTooltip>
  );
}
